import React from 'react';

import { CurationDetails as CurationDetailsComponent } from '../../CurationDetails';

import { NotificationToast } from '../../../NotificationToast';
import { useCurationDetailsController } from './CurationDetails.controller';
import { CurationHero } from '../../components/CurationHero';
import { Page } from '../../components/LayoutStructure';
import { wrapWithCurationDetailsHealthProvider } from '@apps/registry/common/components/Catalog/routes/CurationDetails/CurationDetails.health';
import { Seo } from '@shared/components/Seo';
import { isCatalogItem } from '../../Catalog.types';
import type { StaticRouterProps } from '@shared/core';
import { Redirect, useLocation } from '@shared/core';
import { SearchProductsProvider } from '../CatalogProductsIndex/components/SearchProductsProvider/SearchProductsProvider';
import { deriveQueryStringHelpersFromLocation } from '@shared/core/queryString';

export interface CurationDetailsPageProps
  extends Readonly<{
    isMobile: boolean;
    curationLocator: string;
    staticContext?: StaticRouterProps['context'];
  }> {}

const Root = wrapWithCurationDetailsHealthProvider((props: CurationDetailsPageProps) => {
  const {
    seoTrans,
    filters,
    handleOnLoadMoreProductsButtonClick,
    hasNextPage,
    isFetchingMore,
    notificationText,
    isLoadingSearchResults,
    notificationVisible,
    setNotificationVisible,
    selectedCuration,
    searchResults,
    shopProductConfirmationToastV2Enabled,
    redirectPath,
  } = useCurationDetailsController(props);
  // setup a toolkit by which querystring params will be propagated automatically on redirect
  const location = useLocation();
  const { buildRedirectPath, buildPartialLocation } = deriveQueryStringHelpersFromLocation(location);

  if (redirectPath) {
    if (props.staticContext) {
      // only defined during SSR
      props.staticContext.url = buildRedirectPath(redirectPath);
    } else {
      // https://v5.reactrouter.com/web/api/Redirect
      const redirectLocation = buildPartialLocation(redirectPath);
      return <Redirect to={redirectLocation} />;
    }
  }

  return (
    <Page
      marginBottom={'196px'}
      paddingTop={'0px'}
    >
      <Seo
        title={seoTrans.shopCurationDetailsTitle({ curation: selectedCuration?.curationData?.header?.title?.trim() || 'Curations' })}
        description={seoTrans.shopCurationDetailsDescription({ curation: selectedCuration?.curationData?.header?.title?.trim() || 'Curations' })}
      />
      {selectedCuration?.curationData?.header && (
        <CurationHero
          curationId={selectedCuration.id}
          hero={selectedCuration.curationData.header}
          videoUrl={selectedCuration.curationData.videoUrl}
        />
      )}
      <CurationDetailsComponent
        showSkeleton={!searchResults || isLoadingSearchResults}
        selectedCuration={selectedCuration}
        curationResults={searchResults.filter(isCatalogItem)}
        curationLocator={props.curationLocator}
        filters={filters}
        hasNextPage={hasNextPage}
        isFetchingMore={isFetchingMore}
        onLoadMoreClick={handleOnLoadMoreProductsButtonClick}
      />
      {!shopProductConfirmationToastV2Enabled && (
        <NotificationToast
          notificationText={notificationText}
          notificationVisible={notificationVisible}
          setNotificationVisible={setNotificationVisible}
        />
      )}
    </Page>
  );
});

export const CurationDetails = (props: CurationDetailsPageProps) => {
  return (
    <SearchProductsProvider>
      <Root {...props} />
    </SearchProductsProvider>
  );
};

CurationDetails.displayName = 'CurationDetails';
